import React from 'react';
import { MessageSquareIcon, AlertTriangleIcon, RefreshCwIcon, ArrowLeftIcon } from 'lucide-react';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
export function Screen17_FullReview({
  onBack,
  onNext
}: ScreenProps) {
  return <div className="flex flex-col h-full" data-id="element-736">
      <div className="mb-6" data-id="element-737">
        <h2 className="text-2xl font-bold text-white mb-2" data-id="element-738">Meeting Review</h2>
        <p className="text-slate-400" data-id="element-739">
          Complete AI audit of the safety briefing.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-8" data-id="element-740">
        {/* Meeting Overview */}
        <section data-id="element-741">
          <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-742">
            Meeting Overview
          </h3>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 grid grid-cols-2 gap-y-4 gap-x-2" data-id="element-743">
            <div data-id="element-744">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-745">
                Meeting Type
              </p>
              <p className="text-white font-medium text-sm" data-id="element-746">
                PTP Safety Briefing
              </p>
            </div>
            <div data-id="element-747">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-748">
                Duration
              </p>
              <p className="text-white font-medium text-sm" data-id="element-749">04:36</p>
            </div>
            <div data-id="element-750">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-751">
                Recorded By
              </p>
              <p className="text-white font-medium text-sm" data-id="element-752">Foreman</p>
            </div>
            <div data-id="element-753">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-754">
                Project
              </p>
              <p className="text-white font-medium text-sm" data-id="element-755">
                Downtown Office Tower
              </p>
            </div>
            <div className="col-span-2" data-id="element-756">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-757">
                Location
              </p>
              <p className="text-white font-medium text-sm" data-id="element-758">
                Level 2 Structural Frame
              </p>
            </div>
          </div>
        </section>

        {/* Participants */}
        <section data-id="element-759">
          <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-760">
            Participants
          </h3>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4" data-id="element-761">
            <ul className="space-y-2 text-sm" data-id="element-762">
              <li className="flex justify-between" data-id="element-763">
                <span className="text-white" data-id="element-764">Foreman</span>
                <span className="text-green-400" data-id="element-765">active</span>
              </li>
              <li className="flex justify-between" data-id="element-766">
                <span className="text-white" data-id="element-767">Crane Operator</span>
                <span className="text-green-400" data-id="element-768">active</span>
              </li>
              <li className="flex justify-between" data-id="element-769">
                <span className="text-white" data-id="element-770">Rigger</span>
                <span className="text-green-400" data-id="element-771">active</span>
              </li>
              <li className="flex justify-between" data-id="element-772">
                <span className="text-white" data-id="element-773">Ironworker</span>
                <span className="text-amber-400" data-id="element-774">limited participation</span>
              </li>
              <li className="flex justify-between" data-id="element-775">
                <span className="text-white" data-id="element-776">Laborer</span>
                <span className="text-amber-400" data-id="element-777">limited participation</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Subjects Covered */}
        <section data-id="element-778">
          <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-779">
            Subjects Covered
          </h3>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4" data-id="element-780">
            <ul className="space-y-2 text-sm" data-id="element-781">
              {['Task steps and lift sequence', 'Crane setup and outriggers', 'Rigging inspection', 'Lift path to second floor', 'Exclusion zone', 'Signal person assignment', 'Beam positioning'].map((item, i) => <li key={i} className="flex items-start gap-2 text-slate-300" data-id="element-782">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 flex-shrink-0" data-id="element-783" />
                  {item}
                </li>)}
            </ul>
          </div>
        </section>

        {/* Subjects Missing or Weak */}
        <section data-id="element-784">
          <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-785">
            Subjects Missing or Weak
          </h3>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4" data-id="element-786">
            <ul className="space-y-2 text-sm" data-id="element-787">
              {['Wind conditions before and during lift', 'Emergency response if beam becomes unstable', 'Full crew PPE confirmation', 'Verbal confirmation from all workers'].map((item, i) => <li key={i} className="flex items-start gap-2 text-slate-300" data-id="element-788">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" data-id="element-789" />
                  {item}
                </li>)}
            </ul>
          </div>
        </section>

        {/* AI Recommendation */}
        <section data-id="element-790">
          <div className="flex items-center gap-2 mb-3" data-id="element-791">
            <AlertTriangleIcon className="w-5 h-5 text-orange-500" data-id="element-792" />
            <h3 className="text-sm font-bold text-orange-400 uppercase tracking-wider" data-id="element-793">
              AI Recommendation
            </h3>
          </div>
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4" data-id="element-794">
            <p className="text-orange-100 text-sm leading-relaxed" data-id="element-795">
              Before work begins, repeat a short follow-up meeting covering wind
              conditions, unstable load response, and crew verbal confirmation.
            </p>
          </div>
        </section>

        {/* Example Transcript Snippets */}
        <section data-id="element-796">
          <div className="flex items-center gap-2 mb-3" data-id="element-797">
            <MessageSquareIcon className="w-5 h-5 text-slate-400" data-id="element-798" />
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider" data-id="element-799">
              Example Transcript Snippets
            </h3>
          </div>
          <div className="space-y-3" data-id="element-800">
            {[{
            role: 'Foreman',
            text: 'We lifting beams to level two, crane set here, nobody under the load.'
          }, {
            role: 'Crane Operator',
            text: 'If wind comes up too much or beam starts moving, I stop the lift.'
          }, {
            role: 'Rigger',
            text: 'Tag lines on both ends, keep the load controlled.'
          }, {
            role: 'Foreman',
            text: 'Signal person stays clear and radio check before we go.'
          }].map((snippet, i) => <div key={i} className="bg-slate-900 border border-slate-800 rounded-xl p-4" data-id="element-801">
                <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-802">
                  {snippet.role}
                </p>
                <p className="text-slate-300 text-sm italic" data-id="element-803">
                  "{snippet.text}"
                </p>
              </div>)}
          </div>
        </section>

        {/* Meeting Quality Score */}
        <section data-id="element-804">
          <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-805">
            Meeting Quality Score
          </h3>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center gap-4" data-id="element-806">
            <div className="text-3xl font-black text-white" data-id="element-807">
              72<span className="text-lg text-slate-500" data-id="element-808">/100</span>
            </div>
            <div className="h-8 w-px bg-slate-800" data-id="element-809" />
            <div className="text-amber-400 font-bold uppercase tracking-wider text-sm" data-id="element-810">
              Needs Improvement
            </div>
          </div>
        </section>
      </div>

      <div className="pt-4 space-y-3 bg-slate-950" data-id="element-811">
        <PrimaryButton onClick={onNext} icon={<RefreshCwIcon className="w-5 h-5" data-id="element-813" />} data-id="element-812">
          Send Quiz to Crew
        </PrimaryButton>
        <SecondaryButton onClick={onBack} icon={<ArrowLeftIcon className="w-5 h-5" data-id="element-815" />} data-id="element-814">
          Back to PTP
        </SecondaryButton>
      </div>
    </div>;
}